import tagsService from 'api/tagsService';
import styles from './styles.module.scss';
import { FC, useEffect, useMemo, useState, useRef } from 'react';
import { Props } from './typings';
import { ITag } from 'dto/ITag';
import casesService from 'api/casesService';
import TagBadge from 'components/TagBadge';
import NewInputWithRightIcon from 'components/NewInputWithRightIcon';
import { ICaseSimple } from 'dto/Cases/ICaseSimple';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';

const NewSearchCases: FC<Props> = ({ defaultValue, onChoose, className }) => {
  const { t } = useTranslation();
  const isCase = window.location.href.search('cases') !== -1;
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [value, setValue] = useState('');
  const [stopSearchById, setStopSearchById] = useState(false);
  const [searchedCaseId, setSearchedCaseId] = useState<null | string>(null);
  const [searchList, setSearchList] = useState<ICaseSimple[]>([]);
  const [debouncedText] = useDebounce(value, 300);

  const { data: casesData } = casesService.useGetSimpleCases({
    text: debouncedText,
    id: searchedCaseId ?? undefined,
  });
  const cases: ICaseSimple[] = useMemo(
    //@ts-ignore
    () => casesData?.data.Result.Items ?? [],
    [casesData]
  );

  const { data: tagsData } = tagsService.useGetTags();
  const tags: ITag[] = useMemo(() => tagsData?.data ?? [], [tagsData]);

  useEffect(() => {
    if (searchedCaseId && !stopSearchById) {
      const c = cases[0];
      if (c) {
        setSearchedCaseId(null);
        setValue(`${c.Client ? `${c.Client} |` : ''} ${c.Title}`);
        onChoose?.(c);
        setStopSearchById(true);
      }
    }

    if (defaultValue) {
      const c = cases.find((Case) => Case.Id === defaultValue);
      if (c) {
        setValue(`${c.Client ? `${c.Client} |` : ''} ${c.Title}`);
        onChoose?.(c);
      }
    }

    if (isCase) {
      const packs = window.location.href.split('/');
      const index = packs.findIndex((t) => t === 'cases');
      const caseId = packs.at(index + 1);
      setSearchedCaseId(caseId!);
    }

    setSearchList(cases);
  }, [cases]);

  const handleClick = (cas: ICaseSimple) => {
    setValue(`${cas.Client ? `${cas.Client} |` : ''} ${cas.Title}`);
    setActive(false);
    onChoose?.(cas);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      //@ts-ignore
      if (ref.current && !ref.current.contains(event.target as Element)) {
        setActive(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);

  const buildClient = (c: ICaseSimple) => {
    if (c.Signature && c.Client) {
      return `${c.Signature} | ${c.Client}`;
    }
    if (c.Signature && !c.Client) {
      return c.Signature;
    }

    if (!c.Signature && c.Client) {
      return c.Client;
    }

    return '';
  };

  const Tags = (c: ICaseSimple) => {
    const tagsIds = c.TagIds;

    const findedTags = tags.filter((tag) =>
      tagsIds.find((caseTag) => caseTag === tag.Id)
    );

    return findedTags.map((tag) => <TagBadge tag={tag} />);
  };

  return (
    <div ref={ref} className={`${styles.wrapper} ${className}`}>
      <NewInputWithRightIcon
        onClick={() => setActive(true)}
        value={value}
        onChange={(text) => setValue(text)}
        placeholder={t('cases.caseTitleSearch')}
      />
      {active && (
        <div className={styles.list}>
          <div className={styles.listWrapper}>
            {searchList.map((c) => (
              <div
                onClick={() => handleClick(c)}
                key={c.Id}
                className={styles.box}
              >
                <div className={styles.client}>{buildClient(c)}</div>

                <div className={styles.title}>
                  {Tags(c)}
                  {c.Title}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewSearchCases;
